import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { Suspense, useMemo } from 'react';
import { useIntl } from 'react-intl';

import EmptyChannelsIcon from '@/assets/icons/empty-channels.svg';
import { EmptyState } from '@/components/ui/empty-state';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { Sections } from './sections';

import type { SortBy } from './sort';

interface ChannelsContentProps {
  searchQuery?: string;
  sortBy?: SortBy;
}

export function ChannelsContent({ searchQuery, sortBy }: ChannelsContentProps) {
  const intl = useIntl();
  const { initialSelectedOpeningIds } = useJobPostingContext();

  const {
    data: {
      popular_channels: recommendedChannels,
      free_channels: freeChannels,
      paid_channels: paidChannels,
      indeed_integration_enabled: isIndeedIntegrationEnabled,
    },
  } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.jobPosting.channels,
      initialSelectedOpeningIds,
      searchQuery,
      sortBy,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannels(
        initialSelectedOpeningIds,
        sortBy,
        searchQuery,
      ),
    staleTime: Infinity,
  });

  const hasNoVonqChannels =
    recommendedChannels.vonq_channels.length === 0 &&
    freeChannels.vonq_channels.length === 0 &&
    paidChannels.vonq_channels.length === 0;

  const sections = useMemo(
    () => [
      {
        key: 'popular',
        title: intl.formatMessage({
          defaultMessage: 'Top Picks',
          description: 'Too picked channels for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Boards are selected based on account performance, role type and location.',
          description:
            'Description for top picked channels for posting campaigns',
        }),
        channels: recommendedChannels,
      },
      {
        key: 'free',
        title: intl.formatMessage({
          defaultMessage: 'Free job boards',
          description: 'Free job boards for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage:
            "Boards that post at no cost — no budget, no billing. Includes organic boards and Fountain's own channels like Pool, etc.",
          description: 'Description for free job boards for posting campaigns',
        }),
        channels: freeChannels,
      },
      {
        key: 'paid',
        title: intl.formatMessage({
          defaultMessage: 'Paid boards',
          description: 'Paid job boards for posting campaigns',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Pay-per-click, CPA, or fixed-duration boards.',
          description: 'Description for paid job boards for posting campaigns',
        }),
        channels: paidChannels,
      },
    ],
    [intl, recommendedChannels, freeChannels, paidChannels],
  );

  if (
    recommendedChannels.count === 0 &&
    freeChannels.count === 0 &&
    paidChannels.count === 0
  ) {
    const isSearching = !!searchQuery;
    return (
      <div className="border-fx-neutral-200 flex min-h-0 flex-1 items-center justify-center rounded-lg border bg-white p-4 shadow-sm">
        <EmptyState
          icon={EmptyChannelsIcon}
          header={
            isSearching
              ? intl.formatMessage({
                  defaultMessage: 'No channels found',
                  description:
                    'Empty state header when a search query returns no matching job board channels',
                })
              : intl.formatMessage({
                  defaultMessage: 'No channels available',
                  description:
                    'Empty state header when no publishable job board channels are configured for the account',
                })
          }
          description={
            isSearching
              ? intl.formatMessage({
                  defaultMessage:
                    'No channels match your search. Try a different keyword.',
                  description:
                    'Empty state description when a search query returns no matching job board channels',
                })
              : intl.formatMessage({
                  defaultMessage:
                    'There are no publishable channels configured for this account at this time. Contact your administrator to get channels set up.',
                  description:
                    'Empty state description when no publishable job board channels are configured for the account',
                })
          }
        />
      </div>
    );
  }

  return (
    <div className="border-fx-neutral-200 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-lg border bg-white p-4 shadow-sm">
      {sections
        .filter(section => section.channels.count > 0)
        .map((section, index, filteredSections) => (
          <Sections
            key={section.key}
            title={section.title}
            description={section.description}
            channels={section.channels}
            sectionKey={section.key as 'popular' | 'free' | 'paid'}
            isIndeedIntegrationEnabled={isIndeedIntegrationEnabled}
            isLastSection={index === filteredSections.length - 1}
            forceExpanded={!!searchQuery || hasNoVonqChannels}
          />
        ))}
    </div>
  );
}

export function Channels({ searchQuery, sortBy }: ChannelsContentProps) {
  const { initialSelectedOpeningIds } = useJobPostingContext();

  if (initialSelectedOpeningIds.length === 0) {
    return null;
  }

  return (
    <Suspense fallback={<Channels.Skeleton />}>
      <ChannelsContent searchQuery={searchQuery} sortBy={sortBy} />
    </Suspense>
  );
}

Channels.Skeleton = function ChannelsSkeleton() {
  return (
    <div className="border-fx-neutral-200 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-lg border bg-white p-4 shadow-sm">
      {_range(3).map(index => (
        <Sections.Skeleton key={index} />
      ))}
    </div>
  );
};
